import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { Bell, Search, Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  notificationsService,
  Notification,
} from '@/features/notifications/services/notifications.service';
import { useNotifications } from '@/features/notifications/hooks/useNotifications';
import { Pagination } from '@/components/ui/pagination';
import { NotificationContentModal } from '@/components/notifications/NotificationContentModal';

const PAGE_SIZE = 20;

export const Route = createFileRoute('/_authenticated/notifications')({
  component: NotificationsPage,
});

function useDebouncedValue<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);

  return debouncedValue;
}

function NotificationsPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, 2000);

  // Ao mudar o termo de busca (após debounce), voltar para a página 1
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch]);

  const { markAsRead, markAllAsRead } = useNotifications();

  const searchParam =
    typeof debouncedSearch === 'string' && debouncedSearch.trim() !== ''
      ? debouncedSearch.trim()
      : undefined;

  const { data, isLoading } = useQuery({
    queryKey: ['notifications-paginated', page, searchParam],
    queryFn: () =>
      notificationsService.getNotificationsPaginated(
        page,
        PAGE_SIZE,
        searchParam,
      ),
  });

  // Se a página atual ficar além do total (ex.: após busca), volta para a última válida
  useEffect(() => {
    if (!data) return;
    const pages = Math.max(1, Math.ceil((data.total ?? 0) / PAGE_SIZE) || 1);
    if (page > pages) {
      setPage(pages);
    }
  }, [data, page]);

  const handleMarkAllAsRead = () => {
    markAllAsRead();
    queryClient.invalidateQueries({ queryKey: ['notifications-paginated'] });
  };

  const notifications = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE) || 1);
  const from = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const to = Math.min(page * PAGE_SIZE, total);

  const [selectedNotification, setSelectedNotification] = useState<Notification | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  const handlePageChange = (nextPage: number) => {
    setPage(nextPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.isRead) {
      markAsRead(notification.id);
      queryClient.invalidateQueries({ queryKey: ['notifications-paginated'] });
    }
    setSelectedNotification(notification);
    setModalOpen(true);
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      <div>
        <h1 className="text-3xl font-bold flex items-center gap-2">
          <Bell className="h-8 w-8" />
          Notificações
        </h1>
        <p className="text-muted-foreground">
          Todas as suas notificações. Use a busca para filtrar por título, mensagem ou remetente.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-4">
          <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                type="search"
                placeholder="Buscar notificações..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="pl-9"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={handleMarkAllAsRead}
              disabled={total === 0}
            >
              Marcar todas como lidas
            </Button>
          </div>
        </CardHeader>
        <CardContent className="pt-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="py-12 text-center text-muted-foreground">
              {searchParam
                ? 'Nenhuma notificação encontrada para essa busca.'
                : 'Nenhuma notificação.'}
            </div>
          ) : (
            <>
              <div className="divide-y rounded-md border">
                {notifications.map((notification) => (
                  <div
                    key={notification.id}
                    role="button"
                    tabIndex={0}
                    onClick={() => handleNotificationClick(notification)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleNotificationClick(notification);
                      }
                    }}
                    className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors text-left ${
                      !notification.isRead ? 'bg-blue-50/50 dark:bg-blue-950/20' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{notification.title}</div>
                        <div className="text-sm text-muted-foreground mt-1">
                          {notification.message}
                        </div>
                        <div className="text-xs text-muted-foreground mt-2">
                          {format(new Date(notification.createdAt), "dd 'de' MMMM 'às' HH:mm", {
                            locale: ptBR,
                          })}
                        </div>
                      </div>
                      {!notification.isRead && (
                        <div className="h-2 w-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0" />
                      )}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-sm text-muted-foreground">
                  {total === 0
                    ? 'Nenhuma notificação'
                    : `Mostrando ${from}–${to} de ${total}`}
                  {totalPages > 1 ? ` · Página ${page} de ${totalPages}` : ''}
                </p>
                <Pagination
                  total={total}
                  page={page}
                  limit={PAGE_SIZE}
                  onPageChange={handlePageChange}
                />
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <NotificationContentModal
        open={modalOpen}
        notification={selectedNotification}
        onOpenChange={(open) => {
          setModalOpen(open);
          if (!open) {
            setSelectedNotification(null);
          }
        }}
        onOpenRedirect={
          selectedNotification?.redirectPath
            ? () => navigate({ to: selectedNotification.redirectPath! })
            : undefined
        }
      />
    </div>
  );
}
